import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Scale, LogOut, Lock, ChevronDown, ChevronRight,
  Users, Building2, CreditCard, Key, Palette, Globe, 
  Settings, Database, BarChart3, Shield, RefreshCw, Clock, FileText
} from 'lucide-react';
import axios from 'axios';
import { useBranding } from '../../lib/BrandingContext';
import ModuleWalletDropdown from '../../components/ModuleWalletDropdown';
import BuyCreditsModal from '../../components/BuyCreditsModal';
import { PerformanceStats, WorkflowReport } from '../../components/WorkflowTimer';

const AdminPanel = () => {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    analytics: true,
    performance: true,
    tenants: false,
    applications: false
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    
    if (parsedUser.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }

    fetchStats();
    fetchTenants();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/tenants/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTenants(response.data.tenants || []);
    } catch (err) {
      console.error('Error fetching tenants:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {branding?.logo_url ? (
                <img src={branding.logo_url} alt={branding.company_name || 'Logo'} className="h-10" />
              ) : (
                <div className="flex items-center space-x-2">
                  <Scale className="w-8 h-8 text-blue-600" />
                  <span className="text-xl font-bold text-gray-900">
                    {branding?.company_name || 'TechOrange-Legal'}
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center space-x-2 px-4 py-2 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Shield className="w-4 h-4" />
                <span>Dashboard</span>
              </button>
              
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                <p className="text-xs text-gray-500">@{user?.username}</p>
                <p className="text-xs text-blue-600 font-medium">Super Admin</p>
              </div>
              
              <ModuleWalletDropdown />
              
              <button
                onClick={() => navigate('/password-management')}
                className="flex items-center space-x-2 px-4 py-2 text-sm bg-gray-600 text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Password Management"
              >
                <Lock className="w-4 h-4" />
                <span>Password</span>
              </button>
              
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Left Sidebar */}
        <aside className="w-64 bg-gray-100 border-r border-gray-200 min-h-screen overflow-y-auto">
          <nav className="p-4 space-y-2">
            {/* Analytics Section */}
            <div>
              <button
                onClick={() => toggleSection('analytics')}
                className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-semibold"
              >
                <div className="flex items-center space-x-2">
                  <BarChart3 className="w-4 h-4" />
                  <span>Analytics</span>
                </div>
                {expandedSections.analytics ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {expandedSections.analytics && (
                <div className="ml-4 mt-2 space-y-1">
                  <div className="px-4 py-2 text-sm text-gray-600 cursor-default">
                    Total System Stats
                  </div>
                  <div className="px-4 py-2 text-sm text-gray-600 cursor-default">
                    Tenant Analytics
                  </div>
                  <div className="px-4 py-2 text-sm text-gray-600 cursor-default">
                    Credit Usage
                  </div>
                </div>
              )}
            </div>

            {/* Tenants Section */}
            <div>
              <button
                onClick={() => toggleSection('tenants')}
                className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-semibold"
              >
                <div className="flex items-center space-x-2">
                  <Building2 className="w-4 h-4" />
                  <span>All Tenants</span>
                </div>
                {expandedSections.tenants ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {expandedSections.tenants && (
                <div className="ml-4 mt-2 space-y-1">
                  {tenants.length > 0 ? (
                    tenants.map((tenant) => (
                      <button
                        key={tenant.id}
                        onClick={() => navigate(`/admin/tenants`)}
                        className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded truncate"
                        title={tenant.name || tenant.id}
                      >
                        {tenant.name || tenant.id}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-gray-500 italic">
                      No tenants found
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Applications Section */}
            <div>
              <button
                onClick={() => toggleSection('applications')}
                className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-semibold"
              >
                <div className="flex items-center space-x-2">
                  <Settings className="w-4 h-4" />
                  <span>Applications</span>
                </div>
                {expandedSections.applications ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {expandedSections.applications && (
                <div className="ml-4 mt-2 space-y-1">
                  <button
                    onClick={() => navigate('/admin/tenants')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded flex items-center space-x-2"
                  >
                    <Building2 className="w-4 h-4" />
                    <span>Manage Tenants</span>
                  </button>
                  <button
                    onClick={() => navigate('/admin/users')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded flex items-center space-x-2"
                  >
                    <Users className="w-4 h-4" />
                    <span>Manage Users</span>
                  </button>
                  <button
                    onClick={() => navigate('/admin/billing')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded flex items-center space-x-2"
                  >
                    <CreditCard className="w-4 h-4" />
                    <span>Billing & Usage</span>
                  </button>
                  <button
                    onClick={() => navigate('/admin/company-settings')}
                    className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 rounded flex items-center space-x-2 font-medium"
                  >
                    <FileText className="w-4 h-4" />
                    <span>Company & GST Settings</span>
                  </button>
                  <button
                    onClick={() => navigate('/admin/format-pricing')}
                    className="w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 rounded flex items-center space-x-2 font-medium"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Opinion Format Pricing</span>
                  </button>
                  <button
                    onClick={() => navigate('/admin/module-pricing')}
                    className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded flex items-center space-x-2 font-medium"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Module Pricing & Discounts</span>
                  </button>
                  <button
                    onClick={() => navigate('/admin/module-wallets')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded flex items-center space-x-2"
                  >
                    <CreditCard className="w-4 h-4" />
                    <span>Module Wallets</span>
                  </button>
                  <button
                    onClick={() => navigate('/password-management')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded flex items-center space-x-2"
                  >
                    <Key className="w-4 h-4" />
                    <span>Password Management</span>
                  </button>
                  <button
                    onClick={() => navigate('/admin/branding')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded flex items-center space-x-2"
                  >
                    <Palette className="w-4 h-4" />
                    <span>Branding Settings</span>
                  </button>
                  <button
                    onClick={() => navigate('/admin/domains')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded flex items-center space-x-2"
                  >
                    <Globe className="w-4 h-4" />
                    <span>Domain Management</span>
                  </button>
                  <button
                    onClick={() => navigate('/admin/feature-toggles')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded flex items-center space-x-2"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Feature Toggles</span>
                  </button>
                  <button
                    onClick={() => navigate('/admin/state-management')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded flex items-center space-x-2"
                  >
                    <Database className="w-4 h-4" />
                    <span>State & AI Management</span>
                  </button>
                  <button
                    onClick={() => navigate('/admin/tenant-reset')}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded flex items-center space-x-2 font-medium"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Tenant Data Reset</span>
                  </button>
                </div>
              )}
            </div>
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Admin Control Panel</h2>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Tenants</h3>
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{stats?.total_tenants || 0}</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Users</h3>
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{stats?.total_users || 0}</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Opinions</h3>
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{stats?.total_opinions || 0}</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Credits Used</h3>
                  <CreditCard className="w-5 h-5 text-orange-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{stats?.total_credits_used || 0}</p>
              </div>
            </div>

            {/* Performance Stats Section */}
            <div className="mt-8 bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  Workflow Performance
                </h3>
                <span className="text-sm text-gray-500">Average processing times</span>
              </div>
              <PerformanceStats days={30} />
              
              {/* Workflow Report Table */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    📊 Workflow Timing Report
                  </h3>
                  <span className="text-sm text-gray-500">Individual case timings</span>
                </div>
                <WorkflowReport days={30} limit={50} />
              </div>
            </div>

            {/* Quick Access Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
              <button
                onClick={() => navigate('/admin/tenants')}
                className="bg-white hover:bg-blue-50 border-2 border-blue-200 rounded-xl p-6 text-left transition-all hover:shadow-md"
              >
                <Building2 className="w-8 h-8 text-blue-600 mb-3" />
                <h4 className="font-semibold text-gray-900 mb-1">Manage Tenants</h4>
                <p className="text-sm text-gray-600">Create & manage tenant organizations</p>
              </button>

              <button
                onClick={() => navigate('/admin/users')}
                className="bg-white hover:bg-green-50 border-2 border-green-200 rounded-xl p-6 text-left transition-all hover:shadow-md"
              >
                <Users className="w-8 h-8 text-green-600 mb-3" />
                <h4 className="font-semibold text-gray-900 mb-1">Manage Users</h4>
                <p className="text-sm text-gray-600">User accounts & permissions</p>
              </button>

              <button
                onClick={() => navigate('/admin/billing')}
                className="bg-white hover:bg-purple-50 border-2 border-purple-200 rounded-xl p-6 text-left transition-all hover:shadow-md"
              >
                <CreditCard className="w-8 h-8 text-purple-600 mb-3" />
                <h4 className="font-semibold text-gray-900 mb-1">Billing & Usage</h4>
                <p className="text-sm text-gray-600">Credits & pricing management</p>
              </button>

              <button
                onClick={() => navigate('/password-management')}
                className="bg-white hover:bg-orange-50 border-2 border-orange-200 rounded-xl p-6 text-left transition-all hover:shadow-md"
              >
                <Key className="w-8 h-8 text-orange-600 mb-3" />
                <h4 className="font-semibold text-gray-900 mb-1">Password Management</h4>
                <p className="text-sm text-gray-600">Security & access control</p>
              </button>

              <button
                onClick={() => navigate('/admin/branding')}
                className="bg-white hover:bg-pink-50 border-2 border-pink-200 rounded-xl p-6 text-left transition-all hover:shadow-md"
              >
                <Palette className="w-8 h-8 text-pink-600 mb-3" />
                <h4 className="font-semibold text-gray-900 mb-1">Branding Settings</h4>
                <p className="text-sm text-gray-600">Customize appearance</p>
              </button>

              <button
                onClick={() => navigate('/admin/domains')}
                className="bg-white hover:bg-indigo-50 border-2 border-indigo-200 rounded-xl p-6 text-left transition-all hover:shadow-md"
              >
                <Globe className="w-8 h-8 text-indigo-600 mb-3" />
                <h4 className="font-semibold text-gray-900 mb-1">Domain Management</h4>
                <p className="text-sm text-gray-600">Configure domains & URLs</p>
              </button>

              <button
                onClick={() => navigate('/admin/feature-toggles')}
                className="bg-white hover:bg-cyan-50 border-2 border-cyan-200 rounded-xl p-6 text-left transition-all hover:shadow-md"
              >
                <Settings className="w-8 h-8 text-cyan-600 mb-3" />
                <h4 className="font-semibold text-gray-900 mb-1">Feature Toggles</h4>
                <p className="text-sm text-gray-600">Enable/disable features</p>
              </button>

              <button
                onClick={() => navigate('/admin/state-management')}
                className="bg-white hover:bg-teal-50 border-2 border-teal-200 rounded-xl p-6 text-left transition-all hover:shadow-md"
              >
                <Database className="w-8 h-8 text-teal-600 mb-3" />
                <h4 className="font-semibold text-gray-900 mb-1">State & AI Management</h4>
                <p className="text-sm text-gray-600">AI keys & state config</p>
              </button>
            </div>
          </div>
        </main>
      </div>

      {/* Buy Credits Modal */}
      <BuyCreditsModal open={showBuyCredits} onClose={() => setShowBuyCredits(false)} />
    </div>
  );
};

export default AdminPanel;
